import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  RefreshCw,
  Loader2,
  CalendarDays,
  Phone,
  Mail,
  Images,
  Edit3,
  Search,
  ChevronDown,
  X,
} from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";

const API = `${window.location.origin}/api`;

const PIPELINE_CONFIG = {
  clinico: {
    title: "Promemoria Reparto Clinico",
    subtitle: "Pazienti programmati per follow-up mese per mese · Reparto Clinico",
    pianoCuraPath: "/piano-cura-clinico",
    accent: "teal", // tailwind palette
  },
  orto: {
    title: "Promemoria Ortodonzia",
    subtitle: "Pazienti programmati per follow-up mese per mese · Ortodonzia",
    pianoCuraPath: "/piano-cura-ortodonzia",
    accent: "fuchsia",
  },
};

const ACCENT_CLASSES = {
  teal: {
    border: "border-teal-200",
    badgeBg: "bg-teal-50",
    badgeText: "text-teal-800",
  },
  fuchsia: {
    border: "border-fuchsia-200",
    badgeBg: "bg-fuchsia-50",
    badgeText: "text-fuchsia-800",
  },
};

function formatEuro(v) {
  const n = Number(v || 0);
  if (!n) return "—";
  try {
    return `€ ${n.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  } catch {
    return `€ ${n.toFixed(2)}`;
  }
}

function formatDataInserimento(iso) {
  if (!iso || !/^\d{4}-\d{2}-\d{2}/.test(iso)) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y.slice(2)}`;
}

// ────────────────────────────────────────────────────────────────────
// PhotosViewerDialog: lightweight viewer that fetches photos for a single
// opportunity and renders a simple grid + zoom.
// ────────────────────────────────────────────────────────────────────
function PhotosViewerDialog({ open, onOpenChange, opportunityId, patientName }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !opportunityId) return;
    let cancelled = false;
    setLoading(true);
    setPhotos([]);
    axios
      .get(`${API}/appointments/opportunities/${opportunityId}/photos`, {
        withCredentials: true,
      })
      .then((res) => {
        if (!cancelled) setPhotos(res.data?.photos || []);
      })
      .catch((err) => { console.debug("foto promemoria fetch failed:", err?.message || err); })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, opportunityId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[96vw] w-[96vw] sm:max-w-[80vw] max-h-[88vh] overflow-y-auto"
        data-testid={`promemoria-photos-${opportunityId}`}
      >
        <DialogHeader>
          <DialogTitle>Foto · {patientName || "Paziente"}</DialogTitle>
          <DialogDescription className="text-xs">
            {loading ? "Caricamento…" : `${photos.length} foto trovate`}
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="py-10 text-center text-slate-400 text-sm">
            <Loader2 className="w-5 h-5 animate-spin inline mr-1" /> Caricamento…
          </div>
        ) : photos.length === 0 ? (
          <div className="py-10 text-center text-slate-400 text-sm">
            Nessuna foto associata.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {photos.map((url, i) => (
              <a
                key={url}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="aspect-square overflow-hidden rounded-lg bg-slate-100 border border-slate-200 hover:border-indigo-400 transition-colors"
              >
                <img
                  src={url}
                  alt={`foto-${i}`}
                  loading="lazy"
                  className="w-full h-full object-cover hover:scale-105 transition-transform"
                />
              </a>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ────────────────────────────────────────────────────────────────────
// OpportunityRow
// ────────────────────────────────────────────────────────────────────
function OpportunityRow({ item, kind, highlighted = false }) {
  const [photosOpen, setPhotosOpen] = useState(false);
  const cfg = PIPELINE_CONFIG[kind];

  const editHref = (() => {
    const params = new URLSearchParams();
    params.set("opportunityId", item.id);
    // Flag che attiva: titolo "Promemoria ...", X di chiusura nella
    // Navbar, sblocco foto. Richiesta utente 06/06/2026.
    params.set("promemoria", kind);  // "clinico" o "orto"
    return `${cfg.pianoCuraPath}?${params.toString()}`;
  })();

  // Setta il marker sessionStorage per la X di chiusura. Cliccando
  // sul link la pagina cambia route e la Navbar legge questo flag.
  const handleOpenPianoCura = () => {
    try { sessionStorage.setItem("promemoriaReturnActive", kind); } catch (err) { console.debug("promemoriaReturnActive set failed:", err?.message || err); }
  };

  return (
    <div
      className={`bg-slate-50 rounded-xl border p-4 flex items-start gap-4 transition-all ${
        highlighted
          ? "border-indigo-400 ring-2 ring-indigo-300 bg-indigo-50 shadow-md"
          : "border-slate-200 hover:border-slate-300 hover:bg-white"
      }`}
      data-testid={`promemoria-item-${item.id}`}
    >
      {/* Patient info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="text-sm font-bold text-slate-800 truncate" data-testid={`patient-name-${item.id}`}>
            {item.patient_name || item.name || "—"}
          </h3>
          {item.has_photos && (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
              <Images className="w-3 h-3" /> foto
            </span>
          )}
        </div>
        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-[11px] text-slate-500">
          {item.phone && (
            <span className="flex items-center gap-1">
              <Phone className="w-3 h-3" /> {item.phone}
            </span>
          )}
          {item.email && (
            <span className="flex items-center gap-1 max-w-[220px] truncate">
              <Mail className="w-3 h-3" /> <span className="truncate">{item.email}</span>
            </span>
          )}
          {item.data_inserimento && (
            <span className="flex items-center gap-1">
              <CalendarDays className="w-3 h-3" /> ins. {formatDataInserimento(item.data_inserimento)}
            </span>
          )}
        </div>
        <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
          <div>
            <div className="text-slate-400 uppercase text-[9px] font-bold">Assicurazione</div>
            <div className="text-slate-700 font-semibold truncate" title={item.assicurazione}>
              {item.assicurazione || "—"}
            </div>
          </div>
          <div>
            <div className="text-slate-400 uppercase text-[9px] font-bold">Titolare</div>
            <div className="text-slate-700 font-semibold truncate">
              {item.assigned_user_name || item.medico_name || "—"}
            </div>
          </div>
          <div>
            <div className="text-slate-400 uppercase text-[9px] font-bold">Valore</div>
            <div className="text-emerald-700 font-bold tabular-nums">
              {formatEuro(item.monetary_value)}
            </div>
          </div>
          <div>
            <div className="text-slate-400 uppercase text-[9px] font-bold">Mese</div>
            <div className="text-slate-700 font-semibold">{item.stage_name}</div>
          </div>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <a
          href={editHref}
          onClick={handleOpenPianoCura}
          title="Apri Piano di Cura"
          data-testid={`edit-${item.id}`}
          className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 hover:text-blue-800 border border-blue-200 transition-colors"
        >
          <Edit3 className="w-4 h-4" />
        </a>
        <button
          type="button"
          onClick={() => setPhotosOpen(true)}
          title="Vedi foto"
          data-testid={`view-photos-${item.id}`}
          className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-amber-50 text-amber-700 hover:bg-amber-100 hover:text-amber-800 border border-amber-200 transition-colors disabled:opacity-50"
          disabled={!item.has_photos}
        >
          <Images className="w-4 h-4" />
        </button>
      </div>

      <PhotosViewerDialog
        open={photosOpen}
        onOpenChange={setPhotosOpen}
        opportunityId={item.id}
        patientName={item.patient_name}
      />
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Main page
// ────────────────────────────────────────────────────────────────────
export default function PromemoriaPage({ kind }) {
  const cfg = PIPELINE_CONFIG[kind] || PIPELINE_CONFIG.clinico;
  const accent = ACCENT_CLASSES[cfg.accent];
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [collapsed, setCollapsed] = useState(new Set()); // ym strings
  const [searchTerm, setSearchTerm] = useState("");
  // Highlighted opp id from deep-link (?opp=<id>) — scrolla in vista e
  // applica un breve ring di evidenziazione.
  const [highlightOppId, setHighlightOppId] = useState(() => {
    if (typeof window === "undefined") return "";
    return new URLSearchParams(window.location.search).get("opp") || "";
  });

  const fetchData = useCallback(async () => {
    try {
      const { data: resp } = await axios.get(`${API}/promemoria/list`, {
        params: { pipeline: kind },
        withCredentials: true,
      });
      setData(resp || null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore caricamento promemoria");
    }
  }, [kind]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      await fetchData();
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [fetchData]);

  // Deep-link: dopo che `data` è disponibile, se c'è un opp da evidenziare,
  // scrolliamo l'elemento in vista e togliamo il flag dopo 6s (l'highlight
  // CSS sparisce di conseguenza). Dato che dal 06/06/2026 esiste un unico
  // gruppo "Tutti i mesi" (sempre espanso), non serve più espandere la
  // sezione contenente l'opp.
  useEffect(() => {
    if (!highlightOppId || !data) return;
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-testid="promemoria-item-${highlightOppId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 350);
    const t2 = setTimeout(() => setHighlightOppId(""), 6000);
    return () => { clearTimeout(t); clearTimeout(t2); };
  }, [highlightOppId, data]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchData();
      toast.success("Aggiornato");
    } finally {
      setRefreshing(false);
    }
  };

  const toggleCollapse = (ym) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(ym)) next.delete(ym);
      else next.add(ym);
      return next;
    });
  };

  // Wrap in useMemo for reference stability: senza questo, `data?.groups
  // || []` ricreava un nuovo `[]` ad ogni render quando `data` era null,
  // invalidando il useMemo di `filteredGroups` a valle. Innocuo ma il
  // linter giustamente lo segnalava.
  const groups = useMemo(() => data?.groups || [], [data]);
  const total = data?.total || 0;

  // Filtra opp per nome paziente. Se il termine ha più parole, le cerca tutte
  // (in qualunque ordine) nel nome o nel telefono/email.
  const filteredGroups = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return groups;
    const tokens = q.split(/\s+/).filter(Boolean);
    return groups
      .map((g) => {
        const items = g.items.filter((it) => {
          const hay = [
            it.patient_name || "",
            it.name || "",
            it.phone || "",
            it.email || "",
          ].join(" ").toLowerCase();
          return tokens.every((t) => hay.includes(t));
        });
        return { ...g, items };
      })
      .filter((g) => g.items.length > 0);
  }, [groups, searchTerm]);

  const filteredCount = useMemo(
    () => filteredGroups.reduce((acc, g) => acc + g.items.length, 0),
    [filteredGroups],
  );

  // Richiesta utente 13/06/2026: tornare a mostrare i MESI SEPARATI
  // (Gennaio, Febbraio, …, Maggio, ecc.) e marcare in GIALLO quelli
  // già passati rispetto al mese corrente. Il backend ora include anche
  // i mesi passati con flag `is_past`.
  const monthlyGroups = filteredGroups;

  const headerSubtitle = useMemo(() => {
    if (loading) return cfg.subtitle;
    if (searchTerm.trim()) {
      return `${cfg.subtitle} · ${filteredCount} di ${total} opp · filtro: "${searchTerm.trim()}"`;
    }
    return `${cfg.subtitle} · ${total} opp totali`;
  }, [loading, cfg.subtitle, total, searchTerm, filteredCount]);

  return (
    <div className="min-h-screen bg-gray-100 bg-grid-light pt-[calc(7px+3.5rem+13px)] pb-10 px-4 lg:px-[100px]">
      <div className="w-full">
        <PageHeader
          title={cfg.title}
          subtitle={headerSubtitle}
          actions={
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="w-3.5 h-3.5 text-white/60 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type="search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Cerca paziente…"
                  data-testid="search-patient"
                  className="pl-7 pr-7 py-1.5 rounded-lg bg-white/15 placeholder-white/50 text-white text-xs font-medium border border-white/20 focus:outline-none focus:ring-2 focus:ring-white/40 focus:bg-white/25 transition-colors w-44 sm:w-56"
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm("")}
                    title="Pulisci"
                    data-testid="search-patient-clear"
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-white/70 hover:text-white"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
              <button
                type="button"
                onClick={handleRefresh}
                disabled={refreshing || loading}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/15 hover:bg-white/25 text-white text-xs font-semibold border border-white/20 transition-colors disabled:opacity-50"
                data-testid="refresh-btn"
              >
                {refreshing
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <RefreshCw className="w-3.5 h-3.5" />}
                Aggiorna
              </button>
            </div>
          }
        />
        <div className="bg-white rounded-3xl shadow-sm -mt-8 relative z-10 p-4 sm:p-6 space-y-4">
          {loading ? (
            <div className="text-center py-16 text-slate-400 text-sm">
              <Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Caricamento…
            </div>
          ) : monthlyGroups.length === 0 ? (
            <div className="bg-slate-50 rounded-2xl p-10 text-center text-slate-400">
              {searchTerm.trim()
                ? `Nessuna opportunità trovata per "${searchTerm.trim()}"`
                : "Nessuna opportunità nelle pipeline Promemoria."}
            </div>
          ) : (
            monthlyGroups.map((g) => {
              const isCollapsed = collapsed.has(g.ym);
              // Sfondo giallo per i mesi già trascorsi (is_past flag dal
              // backend). Per quelli correnti/futuri si usa il colore
              // accent della pipeline (teal/fuchsia).
              const past = !!g.is_past;
              const sectionBorder = past ? "border-amber-300" : accent.border;
              const headerBg = past ? "bg-amber-100" : accent.badgeBg;
              const headerText = past ? "text-amber-900" : accent.badgeText;
              return (
                <section
                  key={g.ym}
                  className={`rounded-2xl border-2 ${sectionBorder} overflow-hidden`}
                  data-testid={`promemoria-group-${g.ym}`}
                >
                  <button
                    type="button"
                    onClick={() => toggleCollapse(g.ym)}
                    className={`w-full px-5 py-3 ${headerBg} flex items-center justify-between border-b ${sectionBorder} text-left`}
                    data-testid={`group-toggle-${g.ym}`}
                  >
                    <div className="flex items-center gap-3">
                      <CalendarDays className={`w-5 h-5 ${headerText}`} />
                      <h2 className={`text-base font-bold ${headerText}`}>
                        {g.label}
                      </h2>
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-white text-slate-600 border border-slate-200">
                        {g.items.length} opp
                      </span>
                      {past && (
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-300 text-amber-900 border border-amber-400">
                          Trascorso
                        </span>
                      )}
                    </div>
                    <ChevronDown
                      className={`w-5 h-5 ${headerText} transition-transform ${isCollapsed ? "-rotate-90" : ""}`}
                    />
                  </button>
                  {!isCollapsed && (
                    <div className={`p-4 space-y-2 ${past ? "bg-amber-50/40" : "bg-white"}`}>
                      {g.items.map((it) => (
                        <OpportunityRow
                          key={it.id}
                          item={it}
                          kind={kind}
                          highlighted={it.id === highlightOppId}
                        />
                      ))}
                    </div>
                  )}
                </section>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
